import React, { useState, useEffect } from 'react';
import { LayoutDashboard, Bot, ListChecks, FileSpreadsheet, Database, Upload, BarChart3, Settings, User, LogOut, Building2, Users, FileText, Shield, Trophy, Activity } from 'lucide-react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { hasAdminAccess } from '../utils/rbac';

interface SidebarProps {
  currentView: string;
  onViewChange: (view: string) => void;
}

export function Sidebar({ currentView, onViewChange }: SidebarProps) {
  const { user, signOut } = useAuthenticator((context) => [context.user, context.signOut]);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [debugInfo, setDebugInfo] = useState<any>({});

  // Fetch user session using Amplify v6 API
  useEffect(() => {
    const loadUserGroups = async () => {
      try {
        const session = await fetchAuthSession();
        const accessToken = session.tokens?.accessToken;
        const idToken = session.tokens?.idToken;

        // Get groups from access token or ID token
        const accessGroups = (accessToken?.payload['cognito:groups'] as string[]) || [];
        const idGroups = (idToken?.payload['cognito:groups'] as string[]) || [];

        const groups = accessGroups.length > 0 ? accessGroups : idGroups;
        setUserGroups(groups);

        // Debug info
        const debug = {
          email: user?.signInDetails?.loginId || 'unknown',
          groups: groups,
          isAdmin: groups.includes('admin'),
          accessTokenHasGroups: accessToken?.payload.hasOwnProperty('cognito:groups'),
          idTokenHasGroups: idToken?.payload.hasOwnProperty('cognito:groups'),
          rawAccessTokenGroups: accessToken?.payload['cognito:groups'],
          rawIdTokenGroups: idToken?.payload['cognito:groups'],
          method: 'fetchAuthSession'
        };
        setDebugInfo(debug);

        console.log('fetchAuthSession - Access token payload:', accessToken?.payload);
        console.log('fetchAuthSession - ID token payload:', idToken?.payload);
        console.log('fetchAuthSession - Groups:', groups);
      } catch (error) {
        console.error('Error fetching auth session:', error);
      }
    };

    if (user) {
      loadUserGroups();
    }
  }, [user]);

  const hasAdmin = hasAdminAccess(userGroups);
  const isSuperAdmin = userGroups.includes('super_admin');

  const mainMenuItems = [
    // Leaderboard hidden — not ready for release yet. Data + component intact.
    // { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  ];

  const knowledgeBaseItems = [
    { id: 'ai-helper', label: 'AI Helper', icon: Bot },
  ];

  const jointVenturesItems = [
    { id: 'jv-portal', label: 'JV Portal', icon: Building2 },
  ];

  const leadsItems = [
    { id: 'leads', label: 'Leads', icon: ListChecks },
  ];

  const listManagementItems = [
    { id: 'tax-delinquent-lists', label: 'Tax Delinquent Lists', icon: FileSpreadsheet },
  ];

  const isTaxLatesDataAdmin = userGroups.includes('taxlatesdata');

  const adminMenuItems = [
    // Deal Health lives inside Admin Portal now (Deals tab) — no separate
    // sidebar entry. Admin Portal is the admin's single deal surface.
...(isTaxLatesDataAdmin ? [{ id: 'taxlatesdata-admin', label: 'TaxLatesData Admin', icon: FileSpreadsheet }] : []),
    ...(hasAdmin ? [{ id: 'admin-portal', label: 'Admin Portal', icon: Shield }] : []),
    // Operative Dashboard — super-admin-only monitoring view.
    ...(isSuperAdmin ? [{ id: 'operative-dashboard', label: 'Operative Dashboard', icon: BarChart3 }] : []),
    ...(isSuperAdmin ? [{ id: 'super-admin-portal', label: 'Super Admin Portal', icon: Shield }] : []),
  ];

  const bottomMenuItems = [
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="w-64 bg-gradient-to-b from-tdb-primary to-tdb-secondary h-screen flex flex-col shadow-2xl">
      {/* Logo Section */}
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center justify-center mb-2 bg-white rounded-lg p-4">
          <img
            src="/nfe-logo.png"
            alt="No Flipping Excuses"
            className="w-full h-auto max-w-[180px] object-contain"
          />
        </div>
        <div className="text-xs text-gray-300 font-medium text-center mt-3">JV Portal</div>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 px-3 py-6 overflow-y-auto">
        {/* Main Menu */}
        <div className="space-y-1">
          {mainMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => onViewChange(item.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                  isActive
                    ? 'bg-tdb-accent text-white shadow-lg'
                    : 'text-gray-300 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* Knowledge Base Section - COMMENTED OUT FOR NOW */}
        {/* <div className="mt-6 pt-6 border-t border-white/10">
          <div className="px-4 mb-3">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Knowledge Base</p>
          </div>
          <div className="space-y-1">
            {knowledgeBaseItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => onViewChange(item.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                    isActive
                      ? 'bg-tdb-accent text-white shadow-lg'
                      : 'text-gray-300 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div> */}

        {/* Joint Ventures Section */}
        <div className="mt-6 pt-6 border-t border-white/10">
          <div className="px-4 mb-3">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Joint Ventures</p>
          </div>
          <div className="space-y-1">
            {jointVenturesItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => onViewChange(item.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                    isActive
                      ? 'bg-tdb-accent text-white shadow-lg'
                      : 'text-gray-300 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Lead Management Section - COMMENTED OUT FOR NOW */}
        {/* <div className="mt-6 pt-6 border-t border-white/10">
          <div className="px-4 mb-3">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Lead Management</p>
          </div>
          <div className="space-y-1">
            {leadsItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => onViewChange(item.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                    isActive
                      ? 'bg-tdb-accent text-white shadow-lg'
                      : 'text-gray-300 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div> */}

        {/* List Management Section — hidden from admin + admin_manager; super_admin can still see */}
        {(!hasAdmin || isSuperAdmin) && <div className="mt-6 pt-6 border-t border-white/10">
          <div className="px-4 mb-3">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">List Management</p>
          </div>
          <div className="space-y-1">
            {listManagementItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => onViewChange(item.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                    isActive
                      ? 'bg-tdb-accent text-white shadow-lg'
                      : 'text-gray-300 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>}

        {/* Administration Section */}
        {hasAdmin && adminMenuItems.length > 0 && (
          <div className="mt-6 pt-6 border-t border-white/10">
            <div className="px-4 mb-3">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Administration</p>
            </div>
            <div className="space-y-1">
              {adminMenuItems.map((item) => {
                const Icon = item.icon;
                const isActive = currentView === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => onViewChange(item.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                      isActive
                        ? 'bg-purple-600 text-white shadow-lg'
                        : 'text-gray-300 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </nav>

      {/* Bottom Navigation */}
      <div className="p-3 border-t border-white/10">
        <div className="space-y-1 mb-3">
          {bottomMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => onViewChange(item.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                  isActive
                    ? 'bg-tdb-accent text-white shadow-lg'
                    : 'text-gray-300 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>

        <button
          onClick={signOut}
          className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-gray-300 hover:bg-red-500/20 hover:text-red-300 transition-all"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">Sign Out</span>
        </button>
      </div>
    </div>
  );
}
